var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useState, useEffect, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import camelcaseKeys from 'camelcase-keys';
import axios from 'axios';
import { UserLocationContext } from '@honda-canada/user-location';
import { useAppName, useApiHost, usePageState, useSettings } from '../utils/sitecoreContext';
import safelyStringifyJSON from '../utils/safelyStringifyJSON';
import { isEmpty } from '../utils/object';
import { getFinancialApiPath } from '../utils/financeUtils';
/**
 * Hook to fetch financial data from the Honda REST API.
 * @param {object} options - options object
 * @param {string} options.vehicleType - optional vehicle type to fetch financial data (PSP, ATV, SXS, MPP)
 * @param {function} options.formatter - optional function to format the exported financial data
 * @returns {object} - financial data
 * @returns {boolean} - hasError - true if there was an error fetching the data
 * @returns {boolean} - isFetching - true if the data is being fetched
 * @returns {array} - models - array of models to fetch financial data
 * @returns {function} - setFormatter - set the formatter function
 * @returns {function} - setIsSellingPriceProvince - set the isSellingPriceProvince flag
 * @returns {function} - setModels - set the models to fetch financial data
 */
var useProductFinancial = function (_a) {
    var vehicleType = _a.vehicleType, formatterFn = _a.formatter;
    var appName = useAppName();
    var hondaRestApiHost = useApiHost();
    var settings = useSettings();
    var defaultProvince = settings === null || settings === void 0 ? void 0 : settings.defaultProvince;
    var pageState = usePageState();
    var userLocationContext = useContext(UserLocationContext);
    var _b = userLocationContext || {}, provinceCode = _b.provinceCode, isFetchingUserLocation = _b.isFetchingUserLocation;
    var _c = useState(true), isFetching = _c[0], setIsFetching = _c[1];
    var _d = useState(false), hasError = _d[0], setHasError = _d[1];
    var _e = useState({}), financial = _e[0], setFinancial = _e[1];
    var _f = useState(function () { return formatterFn; }), formatter = _f[0], setFormatter = _f[1];
    var _g = useState(false), isSellingPriceProvince = _g[0], setIsSellingPriceProvince = _g[1];
    var _h = useState([]), models = _h[0], setModels = _h[1];
    var apiPath = getFinancialApiPath(appName, pageState, vehicleType || '');
    var apiUrl = "https://".concat(hondaRestApiHost, "/").concat(apiPath, "/").concat(provinceCode || defaultProvince);
    var setFinancialHandler = function (payload) {
        var payloadModels = payload.models, otherProps = __rest(payload, ["models"]);
        setFinancial(function (prevState) {
            if (isEmpty(prevState)) {
                return payload;
            }
            // TODO: Improve performance using uniqBy
            // const newModels = uniqBy([...prevState.models, ...payloadModels], 'modelKey');
            // return { ...prevState, ...otherProps, models: newModels };
            return __assign(__assign(__assign({}, prevState), otherProps), { models: __spreadArray(__spreadArray([], prevState.models, true), payloadModels, true) });
        });
    };
    var fetchAPI = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var filteredModels, response, payload, err_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 2, , 3]);
                    setIsFetching(true);
                    filteredModels = models === null || models === void 0 ? void 0 : models.filter(function (_a) {
                        var modelKey = _a.modelKey;
                        return !!modelKey;
                    });
                    return [4 /*yield*/, axios.post(apiUrl, filteredModels)];
                case 1:
                    response = _a.sent();
                    if (response === null || response === void 0 ? void 0 : response.data) {
                        payload = camelcaseKeys(response.data, { deep: true });
                        setFinancialHandler(payload);
                    }
                    else {
                        setFinancial({});
                        setHasError(true);
                    }
                    setIsFetching(false);
                    return [3 /*break*/, 3];
                case 2:
                    err_1 = _a.sent();
                    setFinancial({});
                    setHasError(true);
                    setIsFetching(false);
                    return [3 /*break*/, 3];
                case 3: return [2 /*return*/];
            }
        });
    }); }, [apiUrl, safelyStringifyJSON(models)]);
    useEffect(function () {
        if (isFetchingUserLocation) {
            return undefined;
        }
        setIsFetching(false);
        if ((models === null || models === void 0 ? void 0 : models.length) > 0) {
            fetchAPI();
        }
    }, [apiUrl, safelyStringifyJSON(models)]);
    return {
        financial: typeof formatter === 'function' ? formatter(financial, isSellingPriceProvince) : financial,
        hasError: hasError,
        isFetching: isFetching,
        models: models,
        setFormatter: setFormatter,
        setIsSellingPriceProvince: setIsSellingPriceProvince,
        setModels: setModels,
    };
};
useProductFinancial.propTypes = {
    vehicleType: PropTypes.string,
    formatter: PropTypes.func,
};
export default useProductFinancial;
